import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { LogOut, UserPlus, Search, Users, ClipboardCheck, Clock, CheckCircle2, XCircle, Eye, Mail, Phone, Download, PieChart as PieChartIcon, Trash2, ClipboardList, Building2, Link2 } from 'lucide-react';
import api from '@/utils/api';
import AnalyticsCharts from '@/components/AnalyticsCharts';
import CandidateLinkModal from '@/components/CandidateLinkModal';

export default function Dashboard({ user, onLogout }) {
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [stats, setStats] = useState({});
  const [overview, setOverview] = useState(null);
  const [evolution, setEvolution] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [newlyCreatedCandidate, setNewlyCreatedCandidate] = useState(null);
  const [activeTab, setActiveTab] = useState('candidates');
  const navigate = useNavigate();

  const [newCandidate, setNewCandidate] = useState({
    full_name: '',
    email: '',
    phone: '',
    source: '',
    position: '',
    assessment_id: '',
    sector_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterCandidates();
  }, [candidates, searchTerm, statusFilter]);

  const fetchData = async () => {
    try {
      const [candidatesRes, statsRes, overviewRes, evolutionRes, assessmentsRes, sectorsRes] = await Promise.all([
        api.get('/candidates'),
        api.get('/stats'),
        api.get('/stats/overview'),
        api.get('/stats/evolution'),
        api.get('/assessments'),
        api.get('/sectors')
      ]);
      setCandidates(candidatesRes.data);
      setStats(statsRes.data);
      setOverview(overviewRes.data);
      setEvolution(evolutionRes.data.evolution || []);
      setAssessments(assessmentsRes.data);
      setSectors(sectorsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
      // Set empty arrays on error to prevent map errors
      setCandidates([]);
      setStats({});
      setOverview(null);
      setEvolution([]);
      setAssessments([]);
      setSectors([]);
      setFilteredCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const filterCandidates = () => {
    let filtered = Array.isArray(candidates) ? candidates : [];

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCandidates(filtered);
  };

  const handleAddCandidate = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/candidates', newCandidate);
      const candidate = response.data;
      
      setNewlyCreatedCandidate(candidate);
      setShowAddDialog(false);
      setShowLinkModal(true);
      setNewCandidate({ full_name: '', email: '', phone: '', source: '', position: '', assessment_id: '', sector_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar candidato');
      console.error(error);
    }
  };

  const handleDeleteCandidate = async (candidateId, candidateName) => {
    if (!window.confirm(`Tem certeza que deseja excluir ${candidateName}? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      await api.delete(`/candidates/${candidateId}`);
      toast.success('Candidato excluído com sucesso');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir candidato');
      console.error(error);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/candidates/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'candidatos_disc.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar CSV');
      console.error(error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      INVITED: { label: 'Convidado', variant: 'outline', icon: Mail },
      IN_PROGRESS: { label: 'Em Andamento', variant: 'default', icon: Clock },
      COMPLETED: { label: 'Concluído', variant: 'secondary', icon: CheckCircle2 },
      APPROVED_INTERVIEW: { label: 'Aprovado', variant: 'default', icon: CheckCircle2, className: 'bg-green-100 text-green-800' },
      REJECTED: { label: 'Reprovado', variant: 'destructive', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig.INVITED;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      {/* Header */}
      <header className="glass border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{fontFamily: 'Manrope'}}>DISC DRSUPORTI</h1>
              <p className="text-sm text-gray-600">{user?.name} • {user?.role}</p>
            </div>
            <div className="flex items-center gap-3">
              {(user?.role === 'ADMIN') && (
                <>
                  <Button 
                    variant="default"
                    className="bg-[#f97316] hover:bg-[#ea580c] text-white"
                    onClick={() => navigate('/evaluations')}
                    data-testid="manage-evaluations-button"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Criar Link Público
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/sectors')}
                    data-testid="manage-sectors-button"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Gerenciar Setores
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/assessments')}
                    data-testid="manage-assessments-button"
                  >
                    <PieChartIcon className="w-4 h-4 mr-2" />
                    Gerenciar Avaliações
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/admin/perguntas')}
                    data-testid="manage-questions-button"
                  >
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Gerenciar Perguntas
                  </Button>
                </>
              )}
              <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-button">
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
              <Button variant="ghost" onClick={onLogout} data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="shadow-hover border-0 glass" data-testid="stats-total-candidates">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Total de Candidatos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Users className="w-8 h-8 text-indigo-600 mr-3" />
                <p className="text-3xl font-bold text-gray-900">{stats.total_candidates || 0}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-hover border-0 glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Concluídos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <ClipboardCheck className="w-8 h-8 text-green-600 mr-3" />
                <p className="text-3xl font-bold text-gray-900">{stats.completed || 0}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-hover border-0 glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Em Andamento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Clock className="w-8 h-8 text-amber-600 mr-3" />
                <p className="text-3xl font-bold text-gray-900">{stats.in_progress || 0}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-hover border-0 glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Aprovados p/ Entrevista</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mr-3" />
                <p className="text-3xl font-bold text-gray-900">{stats.approved_interview || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="candidates" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Candidatos
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <PieChartIcon className="w-4 h-4" />
              Análise Cíntia 👩‍💻
            </TabsTrigger>
          </TabsList>

          {/* Candidates Tab */}
          <TabsContent value="candidates">
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-candidates-input"
                />
              </div>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                  <SelectValue placeholder="Filtrar por status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  <SelectItem value="INVITED">Convidado</SelectItem>
                  <SelectItem value="IN_PROGRESS">Em Andamento</SelectItem>
                  <SelectItem value="COMPLETED">Concluído</SelectItem>
                  <SelectItem value="APPROVED_INTERVIEW">Aprovado</SelectItem>
                  <SelectItem value="REJECTED">Reprovado</SelectItem>
                </SelectContent>
              </Select>

              {(user?.role === 'ADMIN' || user?.role === 'HR') && (
                <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700" data-testid="add-candidate-button">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Novo Candidato
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Novo Candidato</DialogTitle>
                      <DialogDescription>Cadastre um novo candidato e gere o link de convite</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddCandidate} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nome Completo *</Label>
                        <Input
                          id="name"
                          value={newCandidate.full_name}
                          onChange={(e) => setNewCandidate({...newCandidate, full_name: e.target.value})}
                          required
                          data-testid="new-candidate-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={newCandidate.email}
                          onChange={(e) => setNewCandidate({...newCandidate, email: e.target.value})}
                          required
                          data-testid="new-candidate-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Telefone</Label>
                        <Input
                          id="phone"
                          value={newCandidate.phone}
                          onChange={(e) => setNewCandidate({...newCandidate, phone: e.target.value})}
                          placeholder="(11) 99999-9999"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="position">Vaga</Label>
                        <Input
                          id="position"
                          value={newCandidate.position}
                          onChange={(e) => setNewCandidate({...newCandidate, position: e.target.value})}
                          placeholder="Ex: Suporte N1"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="source">Origem</Label>
                        <Input
                          id="source"
                          value={newCandidate.source}
                          onChange={(e) => setNewCandidate({...newCandidate, source: e.target.value})}
                          placeholder="Ex: LinkedIn, Indicação"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="assessment">Avaliação (Opcional)</Label>
                        <Select 
                          value={newCandidate.assessment_id || undefined} 
                          onValueChange={(value) => setNewCandidate({...newCandidate, assessment_id: value || ''})}
                        >
                          <SelectTrigger id="assessment">
                            <SelectValue placeholder="Avaliação Padrão (Perguntas Globais)" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.isArray(assessments) && assessments.length > 0 ? assessments.map((assessment) => (
                              <SelectItem key={assessment.id} value={assessment.id}>
                                {assessment.name}
                              </SelectItem>
                            )) : null}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">
                          {newCandidate.assessment_id 
                            ? `Avaliação selecionada: ${Array.isArray(assessments) ? assessments.find(a => a.id === newCandidate.assessment_id)?.name || '' : ''}`
                            : 'Deixe vazio para usar as perguntas padrão do sistema'}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sector">Setor (Opcional)</Label>
                        <Select 
                          value={newCandidate.sector_id || undefined} 
                          onValueChange={(value) => setNewCandidate({...newCandidate, sector_id: value || ''})}
                        >
                          <SelectTrigger id="sector">
                            <SelectValue placeholder="Selecione um setor" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.isArray(sectors) && sectors.length > 0 ? sectors.map((sector) => (
                              <SelectItem key={sector.id} value={sector.id}>
                                {sector.name}
                              </SelectItem>
                            )) : null}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">
                          {newCandidate.sector_id 
                            ? `Setor: ${Array.isArray(sectors) ? sectors.find(s => s.id === newCandidate.sector_id)?.name || '' : ''}`
                            : 'O setor ajuda a calcular compatibilidade com o perfil ideal'}
                        </p>
                      </div>
                      <Button type="submit" className="w-full" data-testid="submit-new-candidate">
                        Criar e Gerar Link
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {/* Candidates Table */}
            <Card className="shadow-premium border-0 glass" data-testid="candidates-table">
              <CardHeader>
                <CardTitle className="text-xl">Candidatos ({Array.isArray(filteredCandidates) ? filteredCandidates.length : 0})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Carregando...</p>
                  </div>
                ) : Array.isArray(filteredCandidates) && filteredCandidates.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Nenhum candidato encontrado</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Nome</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Email</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Vaga</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Status</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Data</th>
                          <th className="text-right py-3 px-4 font-semibold text-sm text-gray-700">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(filteredCandidates) && filteredCandidates.length > 0 ? filteredCandidates.map((candidate) => (
                          <tr key={candidate.id} className="border-b hover:bg-gray-50 transition-colors" data-testid={`candidate-row-${candidate.id}`}>
                            <td className="py-3 px-4 font-medium text-gray-900">{candidate.full_name}</td>
                            <td className="py-3 px-4 text-gray-600 text-sm">{candidate.email}</td>
                            <td className="py-3 px-4 text-gray-600 text-sm">{candidate.position || '-'}</td>
                            <td className="py-3 px-4">{getStatusBadge(candidate.status)}</td>
                            <td className="py-3 px-4 text-gray-600 text-sm">
                              {new Date(candidate.created_at).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/candidatos/${candidate.id}`)}
                                  data-testid={`view-candidate-${candidate.id}`}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Ver
                                </Button>
                                {(user?.role === 'ADMIN' || user?.role === 'HR') && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteCandidate(candidate.id, candidate.full_name)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`delete-candidate-${candidate.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )) : null}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsCharts overview={overview} evolution={evolution} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Candidate Link Modal */}
      <CandidateLinkModal
        open={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        candidate={newlyCreatedCandidate}
      />
    </div>
  );
}
